import React, { FC, memo, useCallback, useMemo } from "react";

import Label from "@/components/atoms/inputForms/Label2";
import { Select } from "@/components/atoms/inputForms/Select";
import { TextField } from "@/components/atoms/inputForms/TextField";
import { InputDateField } from "@/components/molecules/inputForms/InputDateField";
// import InputZipField from "@/components/molecules/inputForms/InputZipField";
import { TextFormField } from "@/components/atoms/inputForms/TextFormField";
import { parseValidDate } from "@/utils/date";
import {
  ServiceFormCustomerInputKey,
  ServiceFormCustomerInputs,
  ServiceFormErrors,
} from "@/states/service-form/types";
import { FormProp } from "@/components/editors/template-blocks/service-form/form-items/FormRowsRenderer";
import {
  FormCustomFormCustomFormsExtraJson,
  FormCustomFormCustomFormsOptionsInner,
  ServiceCustomFormsFileType,
  ServiceCustomFormsMaximumFile,
  ServiceCustomFormsType,
} from "@/apis/clients";
import { RadioCardType } from "@/components/atoms/inputForms/RadioCardType";
import { CheckboxCardType } from "@/components/atoms/inputForms/CheckboxCardType";
import { CUSTOM_OTHER_FIELD_ID } from "@/utils/const";
import InputSearchZipField from "@/components/molecules/inputForms/InputSearchZipField";
import clsx from "clsx";
import { convertFromRawRecursively } from "@/utils/convert-page-json";
import { contentJsonAsHtml, contentToHtml } from "@/utils/draft";
import { isObject } from "lodash";
import { ContentState } from "draft-js";
import { CustomFormUploadFile, IFileUpload } from "./CustomFormUploadFile";

/**
 * @param param
 * @returns
 */
export const ExtraHtml: FC<{ extraJson?: FormCustomFormCustomFormsExtraJson; }> = ({ extraJson }) => {
  const contentState: ContentState|null = (extraJson?.content !== undefined && isObject(extraJson?.content)) ? convertFromRawRecursively(Object(extraJson?.content) as object) : null;
  return (
    <>
      {contentState && (
        <div
          style={extraJson?.textAreaStyle ? {...extraJson?.textAreaStyle, height: undefined, lineHeight: "150%"} : {lineHeight: "150%"}}
          dangerouslySetInnerHTML={{__html: contentToHtml(contentState) }}
          className={"flex flex-col max-w-full break-word my-2"}
        />
      )}
    </>
  );
};

type EditableFormInputProps = FormProp & {
  onTextValueChange: (
    value: string | number | undefined,
    filedName?: ServiceFormCustomerInputKey,
  ) => void;
  onBirthdayChange: (date?: Date) => void;
  onChangeRadio?: (
    item: FormCustomFormCustomFormsOptionsInner,
    value?: string,
  ) => void;

  onChangeCheckbox?: (
    item: FormCustomFormCustomFormsOptionsInner,
    value?: string,
  ) => void;
  onChangeFile?: (item: IFileUpload[]) => void;
  value?:
    | number
    | string
    | boolean
    | FormCustomFormCustomFormsOptionsInner[]
    | number[]
    | undefined
    | string[]
    | IFileUpload []
  onChangeDropdown?: (
    item: FormCustomFormCustomFormsOptionsInner,
    value?: string,
  ) => void;
  birthday?: Date | undefined;
  inputOrder: number;
  errors?: ServiceFormErrors;
  inputs?: Partial<ServiceFormCustomerInputs>;
  formCustomFieldKey?: string;
  fieldClassName?: string;
  uploadConfig: {
    maximumFile?: ServiceCustomFormsMaximumFile;
    fileType?: ServiceCustomFormsFileType;
  }
};

const EditableFormInputBase: FC<EditableFormInputProps> = ({
  id,
  label,
  type,
  placeholder,
  options,
  required,
  description,
  descriptionJson,
  extraJson,
  onTextValueChange,
  onBirthdayChange,
  value,
  onChangeRadio,
  onChangeCheckbox,
  onChangeDropdown,
  autoComplete,
  inputOrder,
  errors,
  inputs,
  fieldClassName,
  formCustomFieldKey,
  uploadConfig,
  onChangeFile
}) => {
  let hasOtherPullOption = false;
  if (type === ServiceCustomFormsType.PULLDOWN) {
    const selectedValue = value as FormCustomFormCustomFormsOptionsInner[];
    if (selectedValue?.length) {
      hasOtherPullOption = selectedValue[0].value === CUSTOM_OTHER_FIELD_ID;
    }
  }
  const otherFieldValue = useCallback(() => {
    if (typeof value === "object" && formCustomFieldKey) {
      const selectedValue = value as FormCustomFormCustomFormsOptionsInner[];
      if (selectedValue?.length) {
        return selectedValue.find((item) => {
          return item.value === CUSTOM_OTHER_FIELD_ID;
        })?.otherLabel;
      }
    }
  }, [formCustomFieldKey, value]);

  const onChangeSelectInput = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const selectedOptionValue =
        typeof value === "object"
          ? (value as FormCustomFormCustomFormsOptionsInner[])[0].value
          : "";

      const item = options?.find(
        (option) =>
          option.value?.toString() === selectedOptionValue?.toString(),
      );
      if (item) {
        onChangeDropdown && onChangeDropdown(item, ev?.target?.value);
      }
    },
    [onChangeDropdown, options, value],
  );

  const acceptFile = useMemo(() => {
    if (uploadConfig && uploadConfig.fileType) {
      switch (uploadConfig.fileType) {
        case ServiceCustomFormsFileType.IMAGE:
          return '.jpg,.jpeg,.png,.gif';
        case ServiceCustomFormsFileType.PDF:
          return '.pdf';
        default:
          return '.pdf,.jpg,.jpeg,.png,.gif';
      }
    }
    return '';
  }, [uploadConfig]);

  const fileTypeLabel = useMemo(() => {
    const maximumFile = uploadConfig.maximumFile;
    if (uploadConfig && uploadConfig.fileType) {
      switch (uploadConfig.fileType) {
        case 1:
          return `対応ファイル形式（JPG、PNG、GIF）、1ファイル${maximumFile === ServiceCustomFormsMaximumFile.SINGLE ? 10 : 5}MBまで`;
        case 2:
          return `対応ファイル形式（PDF）、1ファイル${maximumFile === ServiceCustomFormsMaximumFile.SINGLE ? 10 : 5}MBまで`;
        default:
          return `対応ファイル形式（JPG、PNG、GIF、PDF）、1ファイル${maximumFile === ServiceCustomFormsMaximumFile.SINGLE ? 10 : 5}MBまで`;
      }
    }
    return '';
  }, [uploadConfig]);

  switch (type) {
    case "radio":
      return (
        <fieldset id={String(id)}>
          {label !== undefined && (
            <Label
              label={label}
              isRequired={required && label !== undefined && label !== ""}
              description={contentJsonAsHtml(descriptionJson)}
            />
          )}
          <ul className="flex gap-x-4 mt-1 flex-col gap-2">
            {options?.map((item, index) => {
              let isChecked = false;
              if (typeof value === "object") {
                isChecked = !!(
                  value as FormCustomFormCustomFormsOptionsInner[]
                )?.find((valueItem) => valueItem.id === item.id);
              } else if (typeof value === "boolean") {
                isChecked = !!item.value === value;
              } else if (typeof value === "number") {
                isChecked = item.value === value;
              }

              if (item.value === CUSTOM_OTHER_FIELD_ID) {
                return (
                  <li key={index} className="flex">
                    <RadioCardType
                      checked={isChecked}
                      id={`radio-${String(id)}-${String(index)}-${String(
                        inputOrder,
                      )}`}
                      label={"その他"}
                      name={`radio-${String(id)}-${String(index)}-${String(
                        inputOrder,
                      )}`}
                      containerClassName="w-[120px] px-[18px] mr-2"
                      onChange={(e) => {
                        onChangeRadio && onChangeRadio(item);
                      }}
                    />
                    <TextField
                      name={String(id)}
                      value={otherFieldValue()?.toString() || ""}
                      type={"text"}
                      placeholder={"その他の[項目名]"}
                      className={`w-full [&>div]:!mt-0 [&>div]:!h-full `}
                      onBlur={(e) => {
                        onChangeRadio && onChangeRadio(item, e.target.value);
                      }}
                      onChange={(e) => {
                        onChangeRadio && onChangeRadio(item, e.target.value);
                      }}
                    />
                  </li>
                );
              }
              return (
                <li key={index} className="flex">
                  <RadioCardType
                    checked={isChecked}
                    id={`radio-${String(id)}-${String(index)}-${String(
                      inputOrder,
                    )}`}
                    label={item.label}
                    name={`radio-${String(id)}-${String(index)}-${String(
                      inputOrder,
                    )}`}
                    containerClassName="w-full"
                    onChange={(e) => {
                      onChangeRadio && onChangeRadio(item);
                    }}
                  />
                </li>
              );
            })}
          </ul>
        </fieldset>
      );
    case "checkbox":
      return (
        <fieldset id={String(id)}>
          {label !== undefined && (
            <Label
              label={label !== undefined ? label : ""}
              isRequired={required && label !== undefined && label !== ""}
              description={description}
            />
          )}
          <ul className="flex gap-x-4 mt-1 flex-col gap-2">
            {options?.map((item, index) => {
              let isChecked = false;

              if (typeof value === "object") {
                isChecked = !!(
                  value as FormCustomFormCustomFormsOptionsInner[]
                )?.find((valueItem) => valueItem.id === item.id);
              }
              if (item.value === CUSTOM_OTHER_FIELD_ID) {
                return (
                  <li key={index} className="flex">
                    <CheckboxCardType
                      id={`${String(id)}-${String(index)}-${String(
                        inputOrder,
                      )}`}
                      containerClassName="w-[120px] mr-2"
                      label={"その他"}
                      checked={isChecked}
                      onChange={(value) => {
                        onChangeCheckbox && onChangeCheckbox(item);
                      }}
                    />
                    <TextField
                      name={String(id)}
                      value={otherFieldValue()?.toString() || ""}
                      type={"text"}
                      placeholder={"その他の[項目名]"}
                      className={`flex-1 [&>div]:!mt-0 [&>div]:!h-full`}
                      onBlur={(e) => {
                        onChangeCheckbox &&
                          onChangeCheckbox(item, e.target.value);
                      }}
                      onChange={(e) => {
                        onChangeCheckbox &&
                          onChangeCheckbox(item, e.target.value);
                      }}
                    />
                  </li>
                );
              }

              return (
                <li key={index}>
                  <CheckboxCardType
                    id={`${String(id)}-${String(index)}-${String(inputOrder)}`}
                    containerClassName="w-full"
                    label={item.label}
                    checked={isChecked}
                    onChange={(value) => {
                      onChangeCheckbox && onChangeCheckbox(item);
                    }}
                  />
                </li>
              );
            })}
          </ul>
        </fieldset>
      );
    case "age":
      return (
        <>
          <Label
            label={label !== undefined ? label : ""}
            isRequired={required && label !== undefined && label !== ""}
            description={description}
          />
          <div className="flex gap-x-1 items-center">
            <TextField
              defaultValue=""
              id={String(id)}
              name={String(id)}
              type="text"
              className="max-w-[80px] text-right"
              placeholder="20"
              inputMode="numeric"
              autoComplete={autoComplete}
              value={value ? value.toString() : ""}
              onChange={(ev) => {
                onTextValueChange(ev.target.value);
              }}
            />
            <span>歳</span>
          </div>
        </>
      );
    case "select": {
      return (
        <div id={String(id)}>
          <Label
            label={label !== undefined ? label : ""}
            isRequired={required && label !== undefined && label !== ""}
            description={description}
          />
          <Select
            placeholder={placeholder}
            options={
              options?.map((option) => ({
                text: option.label,
                value: option.value,
              })) || []
            }
            value={
              typeof value === "object"
                ? (value as FormCustomFormCustomFormsOptionsInner[])[0].value
                : (value as string | number)
            }
            onChange={(ev) => {
              const item = options?.find(
                (option) => option.value.toString() === ev.toString(),
              );

              if (item) {
                onChangeDropdown && onChangeDropdown(item);
              }
            }}
            className={clsx("mt-1 w-full", fieldClassName)}
          />
          {!!hasOtherPullOption && (
            <li key={options?.length} className="flex mt-2">
              <TextField
                name={String(id)}
                type={"text"}
                placeholder={"その他の[項目名]"}
                className={`w-full [&>div]:!mt-0 [&>div]:!h-full `}
                value={otherFieldValue()?.toString()}
                onBlur={(ev) => onChangeSelectInput(ev)}
                onChange={(ev) => onChangeSelectInput(ev)}
              />
            </li>
          )}
        </div>
      );
    }
    case "zip":
      // return (
      //   <InputZipField
      //     id={String(id)}
      //     nameFirst={`${String(id)}-first`}
      //     nameSecond={`${String(id)}-second`}
      //     label="郵便番号"
      //     isRequired={required && label !== undefined && label !== ""}
      //     onChangeFirst={(ev) => {
      //       onTextValueChange(ev.target.value, "zip1");
      //     }}
      //     onChangeSecond={(ev) => {
      //       onTextValueChange(ev.target.value, "zip2");
      //     }}
      //     valueFirst={inputs?.zip1}
      //     valueSecond={inputs?.zip2}
      //     errors={errors?.customer.zip1 || errors?.customer.zip2}
      //     placeholderFirst="111"
      //     placeholderSecond="1111"
      //     description={description}
      //   />
      // );
      return (
        <InputSearchZipField
          id={String(id)}
          nameFirst={`${String(id)}-first`}
          label="郵便番号"
          isRequired={required && label !== undefined && label !== ""}
          onChangeFirst={(ev) => {
            onTextValueChange(ev.target.value, "zip1");
          }}
          onSetZipAddress={(prefectureId, address) => {
            onTextValueChange(prefectureId, "prefectureId");
            onTextValueChange(address, "address1");
          }}
          valueFirst={inputs?.zip1}
          placeholderFirst="1670053"
          description={contentJsonAsHtml(descriptionJson)}
        />
      );
    case "birthday": {
      return (
        <InputDateField
          label={label !== undefined ? label : ""}
          className="min-w-[220px] "
          description={description}
          value={parseValidDate(value as string)}
          placeholder="2003/01/01"
          hideCalendarIcon
          isRequired={required && label !== undefined && label !== ""}
          onChange={(value) => {
            onBirthdayChange(value || undefined);
          }}
        />
      );
    }

    case "area": {
      return (
        <TextFormField
          id={String(id)}
          label={label !== undefined ? label : ""}
          type={"area"}
          onChange={(ev) => {
            onTextValueChange(ev.target.value);
          }}
          value={value?.toString()}
          placeholder={placeholder}
          className={`w-full`}
          inputClassName="w-full min-h-[83px]"
          isRequired={required && label !== undefined && label !== ""}
          description={description}
          autoComplete={autoComplete}
        />
      );
    }

    case ServiceCustomFormsType.PULLDOWN:
      return (
        <>
          <div id={String(id)}>
            <Label
              label={label !== undefined ? label : ""}
              isRequired={required && label !== undefined && label !== ""}
              description={contentJsonAsHtml(descriptionJson)}
            />
            <Select
              placeholder={placeholder}
              options={
                options?.map((option) => ({
                  text: option.label,
                  value: option.value,
                })) || []
              }
              value={
                typeof value === "object"
                  ? (value as FormCustomFormCustomFormsOptionsInner[])[0].value
                  : (value as string | number)
              }
              onChange={(ev) => {
                const item = options?.find(
                  (option) => option.value.toString() === ev.toString(),
                );

                if (item) {
                  onChangeDropdown && onChangeDropdown(item);
                }
              }}
              className={clsx("mt-1 w-full", fieldClassName)}
            />
            {hasOtherPullOption && (
              <li key={options?.length} className="flex mt-2">
                <TextField
                  name={String(id)}
                  type={"text"}
                  placeholder={"その他の[項目名]"}
                  className={`w-full [&>div]:!mt-0 [&>div]:!h-full `}
                  value={otherFieldValue()?.toString()}
                  onBlur={(ev) => onChangeSelectInput(ev)}
                  onChange={(ev) => onChangeSelectInput(ev)}
                />
              </li>
            )}
          </div>
          <ExtraHtml extraJson={extraJson}/>
        </>
      );

    case ServiceCustomFormsType.INPUT:
      return (
        <>
          <TextFormField
            id={String(id)}
            label={label !== undefined ? label : ""}
            type={"text"}
            onChange={(ev) => {
              onTextValueChange(ev.target.value);
            }}
            value={value ? value.toString() : ""}
            placeholder={placeholder}
            className={`w-full`}
            isRequired={required && label !== undefined && label !== ""}
            description={contentJsonAsHtml(descriptionJson)}
            autoComplete={autoComplete}
          />
          <ExtraHtml extraJson={extraJson}/>
        </>
      );
    case ServiceCustomFormsType.TEXTAREA:
      return (
        <>
          <TextFormField
            id={String(id)}
            label={label !== undefined ? label : ""}
            type={"area"}
            onChange={(ev) => {
              onTextValueChange(ev.target.value);
            }}
            value={value?.toString()}
            placeholder={placeholder}
            className={`w-full`}
            inputClassName="w-full min-h-[83px]"
            isRequired={required && label !== undefined && label !== ""}
            description={contentJsonAsHtml(descriptionJson)}
            autoComplete={autoComplete}
          />
          <ExtraHtml extraJson={extraJson}/>
        </>
      );
    case ServiceCustomFormsType.CHECKBOX:
    case ServiceCustomFormsType.CHECKBOX_ALL:
        return (
          <fieldset id={String(id)}>
            {label !== undefined && (
              <Label
                label={label !== undefined ? label : ""}
                isRequired={required && label !== undefined && label !== ""}
                description={contentJsonAsHtml(descriptionJson)}
              />
            )}
            <ul className="flex gap-x-4 mt-1 flex-col gap-2">
              {options?.map((item, index) => {
                let isChecked = false;

                if (typeof value === "object") {
                  isChecked = !!(
                    value as FormCustomFormCustomFormsOptionsInner[]
                  )?.find((valueItem) => valueItem.id === item.id);
                }
                if (item.value === CUSTOM_OTHER_FIELD_ID) {
                  return (
                    <li key={index} className="flex">
                      <CheckboxCardType
                        id={`${String(id)}-${String(index)}-${String(
                          inputOrder,
                        )}`}
                        containerClassName="w-[120px] mr-2"
                        label={"その他"}
                        checked={isChecked}
                        onChange={(value) => {
                          onChangeCheckbox && onChangeCheckbox(item);
                        }}
                      />
                      <TextField
                        name={String(id)}
                        value={otherFieldValue()?.toString() || ""}
                        type={"text"}
                        placeholder={"その他の[項目名]"}
                        className={`flex-1 [&>div]:!mt-0 [&>div]:!h-full`}
                        onBlur={(e) => {
                          onChangeCheckbox &&
                            onChangeCheckbox(item, e.target.value);
                        }}
                        onChange={(e) => {
                          onChangeCheckbox &&
                            onChangeCheckbox(item, e.target.value);
                        }}
                      />
                    </li>
                  );
                }

                return (
                  <li key={index}>
                    <CheckboxCardType
                      id={`${String(id)}-${String(index)}-${String(inputOrder)}`}
                      containerClassName="w-full"
                      label={item.label}
                      checked={isChecked}
                      onChange={(value) => {
                        onChangeCheckbox && onChangeCheckbox(item);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
            <ExtraHtml extraJson={extraJson}/>
          </fieldset>
        );
    case ServiceCustomFormsType.RADIO:
        return (
          <fieldset id={String(id)}>
            {label !== undefined && (
              <Label
                label={label}
                isRequired={required && label !== undefined && label !== ""}
                description={contentJsonAsHtml(descriptionJson)}
              />
            )}
            <ul className="flex gap-x-4 mt-1 flex-col gap-2">
              {options?.map((item, index) => {
                let isChecked = false;
                if (typeof value === "object") {
                  isChecked = !!(
                    value as FormCustomFormCustomFormsOptionsInner[]
                  )?.find((valueItem) => valueItem.id === item.id);
                } else if (typeof value === "boolean") {
                  isChecked = !!item.value === value;
                } else if (typeof value === "number") {
                  isChecked = item.value === value;
                }

                if (item.value === CUSTOM_OTHER_FIELD_ID) {
                  return (
                    <li key={index} className="flex">
                      <RadioCardType
                        checked={isChecked}
                        id={`radio-${String(id)}-${String(index)}-${String(
                          inputOrder,
                        )}`}
                        label={"その他"}
                        name={`radio-${String(id)}-${String(index)}-${String(
                          inputOrder,
                        )}`}
                        containerClassName="w-[120px] px-[18px] mr-2"
                        onChange={(e) => {
                          onChangeRadio && onChangeRadio(item);
                        }}
                      />
                      <TextField
                        name={String(id)}
                        value={otherFieldValue()?.toString() || ""}
                        type={"text"}
                        placeholder={"その他の[項目名]"}
                        className={`w-full [&>div]:!mt-0 [&>div]:!h-full `}
                        onBlur={(e) => {
                          onChangeRadio && onChangeRadio(item, e.target.value);
                        }}
                        onChange={(e) => {
                          onChangeRadio && onChangeRadio(item, e.target.value);
                        }}
                      />
                    </li>
                  );
                }
                return (
                  <li key={index} className="flex">
                    <RadioCardType
                      checked={isChecked}
                      id={`radio-${String(id)}-${String(index)}-${String(
                        inputOrder,
                      )}`}
                      label={item.label}
                      name={`radio-${String(id)}-${String(index)}-${String(
                        inputOrder,
                      )}`}
                      containerClassName="w-full"
                      onChange={(e) => {
                        onChangeRadio && onChangeRadio(item);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
            <ExtraHtml extraJson={extraJson}/>
          </fieldset>
        );
      case ServiceCustomFormsType.INPUT_UPLOAD:
        return (
          <fieldset id={String(id)}>
            {label !== undefined && (
              <Label
                label={label !== undefined ? label : ""}
                isRequired={required && label !== undefined && label !== ""}
                description={description}
              />
            )}
            <CustomFormUploadFile
              value={value as IFileUpload[]}
              maximumFile={uploadConfig.maximumFile}
              fileTypeLabel={fileTypeLabel}
              acceptFile={acceptFile}
              onChangeFile={onChangeFile}
            />
          </fieldset>
        );
    default:
      return (
        <TextFormField
          id={String(id)}
          // name={String(id)}
          label={label !== undefined ? label : ""}
          type={"text"}
          onChange={(ev) => {
            onTextValueChange(ev.target.value);
          }}
          value={value ? value.toString() : ""}
          placeholder={placeholder}
          className={`w-full ${type === "tel" ? "max-w-[343px]" : ""}`}
          isRequired={required && label !== undefined && label !== ""}
          description={contentJsonAsHtml(descriptionJson)}
          autoComplete={autoComplete}
        />
      );
  }
};

export const EditableFormInput = memo(EditableFormInputBase);
